import { Component } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslatedToastrService, ValidationHandlerService, ModalBaseComponent } from 'lib/Vestra.shared';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { PartnerModel, PartnerSaveModel, VestraService } from 'src/app/services/api-services';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-partner-edit',
    templateUrl: './partner-edit.component.html',
    styleUrls: ['./partner-edit.component.scss'],
    standalone: false,
})
export class PartnerEditComponent extends ModalBaseComponent {
    showModal: boolean = true;

    get showDeleteButton(): boolean {
        return this.item && this.item.id !== 0 && !this.item?.isDeleted;
    }

    get showUndeleteButton(): boolean {
        return this.item && this.item.id !== 0 && this.item?.isDeleted;
    }

    constructor(
        public bsModalRef: BsModalRef,
        private translatedToastrService: TranslatedToastrService,
        protected override validationHandlerService: ValidationHandlerService,
        protected override modalService: BsModalService,
        private vestraService: VestraService,
        protected override spinnerService: NgxSpinnerService
    ) {
        super(spinnerService, modalService, validationHandlerService);
    }

    //input properties
    public title: string;
    public item: PartnerModel;

    //class properties
    private files: { [key: string]: File | null } = {
        brandImage: null,
        coverImage: null,
    };

    public fileNames: { [key: string]: string } = {
        brandImage: '',
        coverImage: '',
    };

    private getFormGroup(): FormGroup {
        return new FormGroup({
            name: new FormControl('', [Validators.required]),
            description: new FormControl('', [Validators.required]),
            phoneNumber: new FormControl('', [Validators.required]),
            email: new FormControl('', [Validators.email, Validators.required]),
            websiteUrl: new FormControl('', [Validators.required]),
            gpsCoordinate: new FormControl(''),
        });
    }

    public form: FormGroup = this.getFormGroup();

    public init() {
        this.form = this.getFormGroup();

        if (this.item) {
            this.form.patchValue(this.item);
            if (this.item.brandImageUrl) {
                this.setImgSrc('selected-brandImage', this.item.brandImageUrl);
            }
            if (this.item.coverImageUrl) {
                this.setImgSrc('selected-coverImage', this.item.coverImageUrl);
            }
        } else {
            this.clearSelectedImages();
            this.clearImageFileInput();
        }
    }

    public ngAfterViewInit() {
        this.init();
    }

    public onFileSelected(event: any, type: 'brandImage' | 'coverImage'): void {
        const fileInput = event.target as HTMLInputElement;

        if (fileInput.files && fileInput.files.length > 0) {
            const file = fileInput.files[0];

            if (!this.isImageFileExtensionGood(file)) {
                this.onClearImage(type);
                this.translatedToastrService.warning('common.image_is_required', 'common.warning');
                return;
            }

            this.files[type] = file;
            this.fileNames[type] = file.name;

            const reader = new FileReader();
            reader.onloadend = () => {
                this.setImgSrc(`selected-${type}`, reader.result as any);
            };

            reader.readAsDataURL(file);
        }
    }

    public onClearImage(type: 'brandImage' | 'coverImage'): void {
        this.setEmptyFileInputValue(`${type}-file-input`);
        this.setEmptyImgSrc(`selected-${type}`);

        this.files[type] = null;
        this.fileNames[type] = '';
    }

    private clearSelectedImages() {
        this.setEmptyImgSrc('selected-brandImage');
        this.setEmptyImgSrc('selected-coverImage');
        this.files['brandImage'] = null;
        this.files['coverImage'] = null;
    }

    private clearImageFileInput() {
        this.setEmptyFileInputValue('brandImage-file-input');
        this.setEmptyFileInputValue('coverImage-file-input');
    }

    public onDeleteConfirm = async () => {
        this.showModal = false;
        if (this.item.isDeleted) {
            this.openConfirmDialog(
                undefined,
                () => this.deleteItem(this.item.id),
                () => this.onCancel(),
                'common.undelete_title',
                'common.undelete_confirm'
            );
        } else {
            this.openConfirmDialog(
                undefined,
                () => this.deleteItem(this.item.id),
                () => this.onCancel()
            );
        }
    };

    public onCancel() {
        this.showModal = true;
    }

    public deleteItem(itemIdToDeleteId: number): void {
        this.handleSpinner(
            firstValueFrom(this.vestraService.deleteOrUndeletePartner(itemIdToDeleteId))
                .then(res => {
                    this.bsModalRef.hide();
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    public onSave = async (): Promise<any> => {
        if (this.validationHandlerService.checkFormValid(this.form)) {
            let hasBrandImage = this.files['brandImage'] != null || this.item?.brandImageUrl;
            let hasCoverImage = this.files['coverImage'] != null || this.item?.coverImageUrl;
            if (!this.item && (!hasBrandImage || !hasCoverImage)) {
                this.translatedToastrService.warning('common.image_is_required', 'common.warning');
                return;
            }

            let partner: PartnerModel = <PartnerModel>this.form.getRawValue();
            partner.id = this.item?.id;

            let itemToSave: PartnerSaveModel = {
                partner: partner,
                brandImage: <any>this.files['brandImage'],
                coverImage: <any>this.files['coverImage'],
            };

            return this.handleSpinner(
                firstValueFrom(this.vestraService.updatePartner(itemToSave))
                    .then(res => {
                        this.onClose(this.bsModalRef, 'save_success');
                        return Promise.resolve(res);
                    })
                    .catch((error: any) => {
                        this.validationHandlerService.handleServerError(error, this.form);
                    })
            );
        }
    };
}
